'use client'

import { useState } from 'react'

interface ReferralCodeModalProps {
  isOpen: boolean
  onContinue: (referralCode: string) => void
  onProceedWithout: () => void
  isSubmitting?: boolean
}

export function ReferralCodeModal({ isOpen, onContinue, onProceedWithout, isSubmitting = false }: ReferralCodeModalProps) {
  const [referralCode, setReferralCode] = useState('')
  const [error, setError] = useState('')

  if (!isOpen) return null

  const handleContinue = () => {
    setError('')
    const trimmedCode = referralCode.trim()

    // Validation: non-empty and minimum length
    if (!trimmedCode) {
      setError('Please enter a referral code')
      return
    }

    if (trimmedCode.length < 3) {
      setError('Referral code must be at least 3 characters')
      return
    }

    onContinue(trimmedCode)
  }

  const handleProceedWithout = () => {
    setError('')
    setReferralCode('')
    onProceedWithout()
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-body">
          <div className="modal-text-content">
            <h2 className="modal-title">Do you have any referral code?</h2>

            <p className="modal-description">
              If you have a referral code, enter it below to get started. You can also proceed without one.
            </p>

            <div className="input-group">
              <input
                type="text"
                className={`referral-input ${error ? 'input-error' : ''}`}
                placeholder="Enter referral code"
                value={referralCode}
                onChange={(e) => {
                  setReferralCode(e.target.value)
                  setError('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleContinue()
                  }
                }}
                autoFocus
              />
              {error && <p className="error-message">{error}</p>}
            </div>

            <div className="modal-buttons">
              <button className="button-continue" onClick={handleContinue} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Continue'}
              </button>
              <button className="button-proceed" onClick={handleProceedWithout} disabled={isSubmitting}>
                Proceed without referral
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          animation: fadeIn 0.2s ease-out;
          pointer-events: auto;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-content {
          background: white;
          border-radius: 24px;
          padding: 3rem;
          max-width: 580px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease-out;
          pointer-events: auto;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-body {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
        }

        .modal-text-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .modal-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #5B4EF5;
          margin: 0 0 1.25rem 0;
          line-height: 1.3;
        }

        .modal-description {
          font-size: 1rem;
          color: #6B7280;
          line-height: 1.6;
          margin: 0 0 1.5rem 0;
          max-width: 460px;
        }

        .input-group {
          width: 100%;
          margin-bottom: 1.5rem;
        }

        .referral-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 1rem;
          color: #1f2937;
          transition: all 0.3s ease;
          box-sizing: border-box;
          background: white;
        }

        .referral-input:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .referral-input.input-error {
          border-color: #EF4444;
        }

        .referral-input.input-error:focus {
          border-color: #EF4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .error-message {
          color: #EF4444;
          font-size: 0.875rem;
          margin: 0.5rem 0 0 0;
        }

        .modal-buttons {
          display: flex;
          gap: 1rem;
          width: 100%;
        }

        .button-continue {
          flex: 1;
          padding: 0.875rem 2rem;
          background: linear-gradient(135deg, #6B5CE7 0%, #5B4EF5 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(91, 78, 245, 0.3);
        }

        .button-continue:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(91, 78, 245, 0.4);
          background: linear-gradient(135deg, #7C6EE8 0%, #6B5CE7 100%);
        }

        .button-continue:active {
          transform: translateY(0);
        }

        .button-proceed {
          flex: 1;
          padding: 0.875rem 2rem;
          background: white;
          color: #6B7280;
          border: 2px solid #E5E7EB;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .button-proceed:hover {
          border-color: #D1D5DB;
          color: #4B5563;
          background: #F9FAFB;
        }

        .button-proceed:active {
          transform: scale(0.98);
        }

        @media (max-width: 640px) {
          .modal-content {
            padding: 2rem 1.5rem;
            max-width: 95%;
          }

          .modal-title {
            font-size: 1.5rem;
          }

          .modal-description {
            font-size: 0.9375rem;
          }

          .modal-buttons {
            flex-direction: column;
            max-width: 100%;
          }

          .button-continue,
          .button-proceed {
            width: 100%;
            padding: 0.875rem 1.5rem;
          }
        }
      `}</style>
    </div>
  )
}

